import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { applyNodeChanges, type NodeChange } from "@xyflow/react";
import { useGetManufacturingItemInit } from "../../hooks/Manufacturing/useGetManufacturingItemInit";
import { useGetSalesOrderLineExistence, type ILineExistenceInfo } from "../../hooks/SalesOrderLine/useGetSalesOrderLineExistence";
import { getDesignItemsByDesignID, type IDesignItemInfo } from "../../hooks/ReleasedProduct/useGetItemsByDesignID";
import { getReleasedProductComparison } from "../../hooks/ReleasedProduct/useGetReleasedProductComparison";
import { getProductComparison } from "../../hooks/Product/useGetProductComparison";
import { getProductD365, type IProductD365 } from "../../hooks/Product/useGetProductD365";
import { getReleasedProductD365, type IReleasedProductD365 } from "../../hooks/ReleasedProduct/useGetReleasedProductD365";
import { getRouteVersionByItem, type IRouteVersionByItem } from "../../hooks/Route/useGetRouteVersionByItem";
import { getRouteOperationsCS, type IRouteOperationCS } from "../../hooks/Route/useGetRouteOperationsD365";
import { getRouteHeaderExistsD365 } from "../../hooks/Route/useGetRouteHeaderExistsD365";
import { getRouteOperationPropertiesCS, type IRouteOperationPropertyCS } from "../../hooks/Route/useGetRouteOperationPropertiesCS";
import { getRouteOperationRequirementsCS, type IRouteOperationRequirementCS } from "../../hooks/Route/useGetRouteOperationRequirementsCS";
import { getRouteHeaderComparison } from "../../hooks/Route/useGetRouteHeaderComparison";
import { getRouteVersionComparison } from "../../hooks/Route/useGetRouteVersionComparison";
import { getBomLinesByRouteCS, type IBomLineCS } from "../../hooks/Route/useGetBomLinesCS";
import { getReleasedProductExistsD365 } from "../../hooks/ReleasedProduct/useGetReleasedProductExistsD365";
import { getManufacturingRoute } from "../../hooks/Manufacturing/useGetManufacturingRoute";
import { getRouteVersionByItemD365, type IRouteVersionD365 } from "../../hooks/Route/useGetRouteVersionByItemD365";
import { getRouteOperationsByRouteD365, type IRouteOperationD365 } from "../../hooks/Route/useGetRouteOperationsByRouteD365";
import { getRouteOperationPropertiesByRouteD365, type IRouteOperationPropertyD365 } from "../../hooks/Route/useGetRouteOperationPropertiesByRouteD365";
import { getRouteOperationRequirementsByRouteD365, type IRouteOperationRequirementD365 } from "../../hooks/Route/useGetRouteOperationRequirementsByRouteD365";
import { getBomVersionByItemD365, type IBomVersionD365 } from "../../hooks/Route/useGetBomVersionByItemD365";
import { getBomLinesByBomD365, type IBomLineD365 } from "../../hooks/Route/useGetBomLinesByBomD365";
import { splitPascalCase } from "../../utilities/ObjectUtilities";
import { useExplorerStore } from "../../stores/useExplorerStore";
import { useShallow } from "zustand/react/shallow";
import { applyDagreLayout } from "./useAutoLayout";
import {
    buildComparisonQueryMap,
    buildD365QueryMap,
    computeStatus,
    EMPTY_BOM_LINES_D365,
    EMPTY_OPERATIONS_D365,
    EMPTY_OP_PROPERTIES_D365,
    EMPTY_OP_REQUIREMENTS_D365,
} from "./graphHelpers";
import type { IComparisonInfo } from "../../declarations/types";
import type {
    ExplorerNode,
    ExplorerEdge,
    ExplorerNodeStatus,
} from "../../declarations/IExplorerTypes";
import type { IReadVerbPayload } from "../../declarations/IReadVerbPayload";

export function useManufacturingGraph(orderItemID: number) {
    const setSelectedNodeId = useExplorerStore((s) => s.setSelectedNodeId);
    const selectedNodeId = useExplorerStore((s) => s.selectedNodeId);
    const {
        expandedReleasedProducts,
        expandedOperations,
        expandedBomLines,
    } = useExplorerStore(useShallow((s) => ({
        expandedReleasedProducts: s.expandedReleasedProducts,
        expandedOperations: s.expandedOperations,
        expandedBomLines: s.expandedBomLines,
    })));
    const toggleReleasedProductExpanded = useExplorerStore((s) => s.toggleReleasedProductExpanded);
    const toggleOperationExpanded = useExplorerStore((s) => s.toggleOperationExpanded);
    const toggleBomLineExpanded = useExplorerStore((s) => s.toggleBomLineExpanded);

    // Ref + version counter used to break the cyclic dependency between d365FallbackRPKeys and
    // d365BomLineMap. The ref holds the latest d365BomLineMap value; the version counter
    // is a stable primitive that triggers d365FallbackRPKeys to recompute when d365BomLineMap changes.
    const d365BomLineMapRef = useRef<Map<string, IBomLineD365[]>>(new Map());
    const [d365BomLineMapVersion, setD365BomLineMapVersion] = useState(0);

    const { data: init, isLoading: initLoading } =
        useGetManufacturingItemInit(orderItemID);

    const onNodeSelect = useCallback(
        (nodeId: string) => {
            const current = useExplorerStore.getState().selectedNodeId;
            setSelectedNodeId(nodeId === current ? null : nodeId);
        },
        [setSelectedNodeId],
    );

    const onReleasedProductSelect = useCallback(
        (nodeId: string) => {
            // nodeId is "ReleasedProduct-itemNumber|dataAreaId"
            const rpKey = nodeId.replace("ReleasedProduct-", "");
            toggleReleasedProductExpanded(rpKey);
        },
        [toggleReleasedProductExpanded],
    );

    const onOperationSelect = useCallback(
        (rpKey: string, operationNumber: number) => {
            const opKey = `${rpKey}|${operationNumber}`;
            toggleOperationExpanded(opKey);
        },
        [toggleOperationExpanded],
    );

    const onBomLineSelect = useCallback(
        (itemNumber: string, dataAreaId: string) => {
            const bomLineKey = `${itemNumber}|${dataAreaId}`;
            toggleBomLineExpanded(bomLineKey);
        },
        [toggleBomLineExpanded],
    );

    // Existence lookup — same proc as the SO graph, keyed by orderID.
    const linesPayload: IReadVerbPayload = useMemo(
        () => ({
            keys: {
                dataAreaId: init?.dataAreaId ?? "",
                salesOrderNumber: "",
                orderID: init?.orderID ?? 0,
            },
        }),
        [init?.dataAreaId, init?.orderID],
    );

    const { data: existenceData } = useGetSalesOrderLineExistence(linesPayload);

    const existenceMap = useMemo(() => {
        const map = new Map<string, ILineExistenceInfo>();
        if (existenceData) {
            for (const e of existenceData) {
                if (e.inventoryLotId) {
                    map.set(e.inventoryLotId.toLowerCase(), e);
                }
            }
        }
        return map;
    }, [existenceData]);

    // ---------- Design items + make/buy split ----------
    const designIDs = useMemo(() => {
        if (!init?.designID || init.designID <= 0) {
            return [] as number[];
        }
        return [init.designID];
    }, [init?.designID]);

    const itemsByDesignQueries = useQueries({
        queries: designIDs.map((designID) => ({
            queryKey: ["GetDesignItemsByDesignID", designID],
            queryFn: () => getDesignItemsByDesignID(designID),
            enabled: designID > 0,
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const itemsByDesignMap = useMemo(() => {
        const map = new Map<number, IDesignItemInfo[]>();
        designIDs.forEach((designID, index) => {
            const q = itemsByDesignQueries[index];
            if (q?.data) {
                map.set(designID, q.data);
            }
        });
        return map;
    }, [designIDs, itemsByDesignQueries]);

    const productMakeMap = useMemo(() => {
        const map = new Map<string, boolean>();
        if (!init?.itemNumber) {
            return map;
        }
        const designID = init.designID ?? 0;
        const items = designID > 0 ? itemsByDesignMap.get(designID) : undefined;
        const isMake = items?.some((i) => i.make) ?? false;
        map.set(init.itemNumber, isMake);
        return map;
    }, [init?.itemNumber, init?.designID, itemsByDesignMap]);

    // ---------- Released Product comparison queries (make items only) ----------
    const rpComparisonKeys = useMemo(() => {
        const keys: { itemNumber: string; dataAreaId: string; lineNum: number }[] = [];
        if (!init?.itemNumber) {
            return keys;
        }
        if (!productMakeMap.get(init.itemNumber)) {
            return keys;
        }
        const designID = init.designID ?? 0;
        const items = designID > 0 ? itemsByDesignMap.get(designID) : undefined;

        if (items && items.length > 0) {
            for (const item of items) {
                keys.push({
                    itemNumber: item.name,
                    dataAreaId: item.entity,
                    lineNum: init.lineNumber,
                });
            }
        } else {
            keys.push({
                itemNumber: init.itemNumber,
                dataAreaId: init.dataAreaId ?? "",
                lineNum: init.lineNumber,
            });
        }
        return keys;
    }, [init, itemsByDesignMap, productMakeMap]);

    const releasedProductQueries = useQueries({
        queries: rpComparisonKeys.map(({ itemNumber, dataAreaId }) => ({
            queryKey: ["GetReleasedProductComparison", { itemNumber, dataAreaId }],
            queryFn: () =>
                getReleasedProductComparison({
                    keys: { itemNumber, dataAreaId, itemID: 0 },
                }),
            enabled: Boolean(itemNumber && dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const releasedProductMap = useMemo(
        () => buildComparisonQueryMap(rpComparisonKeys, releasedProductQueries, (k) => `${k.itemNumber}|${k.dataAreaId}`),
        [rpComparisonKeys, releasedProductQueries],
    );

    // ---------- Product comparison (make items only) ----------
    const uniqueProductNumbers = useMemo(() => {
        if (!init?.itemNumber) {
            return [] as string[];
        }
        if (!productMakeMap.get(init.itemNumber)) {
            return [] as string[];
        }
        return [init.itemNumber];
    }, [init?.itemNumber, productMakeMap]);

    const productQueries = useQueries({
        queries: uniqueProductNumbers.map((productNumber) => ({
            queryKey: ["GetProductComparison", { productNumber }],
            queryFn: () =>
                getProductComparison({
                    keys: { productNumber, productID: 0 },
                }),
            enabled: Boolean(productNumber),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const productMap = useMemo(
        () => buildComparisonQueryMap(uniqueProductNumbers, productQueries, (pn) => pn),
        [uniqueProductNumbers, productQueries],
    );

    // ---------- Buy/Stock product + RP queries (D365-only) ----------
    const buyProductNumbers = useMemo(() => {
        if (!init?.itemNumber) {
            return [] as string[];
        }
        if (productMakeMap.get(init.itemNumber)) {
            return [] as string[];
        }
        return [init.itemNumber];
    }, [init?.itemNumber, productMakeMap]);

    const buyProductQueries = useQueries({
        queries: buyProductNumbers.map((productNumber) => ({
            queryKey: ["GetProductD365", { productNumber }],
            queryFn: () =>
                getProductD365({
                    keys: { productNumber, productID: 0 },
                }),
            enabled: Boolean(productNumber),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const buyProductMap = useMemo(
        () => buildD365QueryMap<string, IProductD365>(buyProductNumbers, buyProductQueries, (pn) => pn),
        [buyProductNumbers, buyProductQueries],
    );

    const buyRPKeys = useMemo(() => {
        if (!init?.itemNumber) {
            return [] as { itemNumber: string; dataAreaId: string }[];
        }
        if (productMakeMap.get(init.itemNumber)) {
            return [] as { itemNumber: string; dataAreaId: string }[];
        }
        return [{ itemNumber: init.itemNumber, dataAreaId: init.dataAreaId ?? "" }];
    }, [init?.itemNumber, init?.dataAreaId, productMakeMap]);

    const buyRPQueries = useQueries({
        queries: buyRPKeys.map(({ itemNumber, dataAreaId }) => ({
            queryKey: ["GetReleasedProductD365", { itemNumber, dataAreaId }],
            queryFn: () =>
                getReleasedProductD365({
                    keys: { itemNumber, dataAreaId, itemID: 0 },
                }),
            enabled: Boolean(itemNumber && dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const buyRPMap = useMemo(
        () => buildD365QueryMap<{ itemNumber: string; dataAreaId: string }, IReleasedProductD365>(
            buyRPKeys,
            buyRPQueries,
            (k) => `${k.itemNumber}|${k.dataAreaId}`,
        ),
        [buyRPKeys, buyRPQueries],
    );

    // ---------- Route version resolution (CS) ----------
    const expandedRPKeys = useMemo(() => {
        const keys: { itemNumber: string; dataAreaId: string }[] = [];
        const seen = new Set<string>();
        for (const rpKey of expandedReleasedProducts) {
            const [itemNumber, dataAreaId] = rpKey.split("|");
            if (itemNumber && dataAreaId && !seen.has(rpKey)) {
                seen.add(rpKey);
                keys.push({ itemNumber, dataAreaId });
            }
        }
        // BOM-sourced RPs that are expanded also need route resolution
        for (const bomKey of expandedBomLines) {
            if (!seen.has(bomKey)) {
                const [itemNumber, dataAreaId] = bomKey.split("|");
                if (itemNumber && dataAreaId) {
                    seen.add(bomKey);
                    keys.push({ itemNumber, dataAreaId });
                }
            }
        }
        return keys;
    }, [expandedReleasedProducts, expandedBomLines]);

    const routeVersionQueries = useQueries({
        queries: expandedRPKeys.map(({ itemNumber, dataAreaId }) => ({
            queryKey: ["GetRouteVersionByItem", { itemNumber, dataAreaId }],
            queryFn: () => getRouteVersionByItem(itemNumber, dataAreaId),
            enabled: Boolean(itemNumber && dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const routeVersionMap = useMemo(() => {
        const map = new Map<string, IRouteVersionByItem | null>();
        expandedRPKeys.forEach((key, index) => {
            const q = routeVersionQueries[index];
            if (q?.isFetched) {
                map.set(`${key.itemNumber}|${key.dataAreaId}`, q.data ?? null);
            }
        });
        return map;
    }, [expandedRPKeys, routeVersionQueries]);

    const routeOperationKeys = useMemo(() => {
        const keys: { rpKey: string; csRouteID: number; routeId: string; dataAreaId: string }[] = [];
        for (const [rpKey, rv] of routeVersionMap) {
            if (rv?.csRouteID && rv?.routeId && rv?.dataAreaID) {
                keys.push({ rpKey, csRouteID: rv.csRouteID, routeId: rv.routeId, dataAreaId: rv.dataAreaID });
            }
        }
        return keys;
    }, [routeVersionMap]);

    const routeOperationQueries = useQueries({
        queries: routeOperationKeys.map(({ csRouteID }) => ({
            queryKey: ["GetRouteOperationsCS", csRouteID],
            queryFn: () => getRouteOperationsCS(csRouteID),
            enabled: csRouteID > 0,
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const routeDataMap = useMemo(() => {
        const map = new Map<string, {
            routeId: string;
            operations: IRouteOperationCS[];
            isLoading: boolean;
            isError: boolean;
        }>();
        routeOperationKeys.forEach((key, index) => {
            const q = routeOperationQueries[index];
            map.set(key.rpKey, {
                routeId: key.routeId,
                operations: q?.data ?? [],
                isLoading: q?.isLoading ?? true,
                isError: q?.isError ?? false,
            });
        });
        return map;
    }, [routeOperationKeys, routeOperationQueries]);

    const routeExistsQueries = useQueries({
        queries: routeOperationKeys.map(({ routeId, dataAreaId }) => ({
            queryKey: ["GetRouteHeaderExistsD365", { routeId, dataAreaId }],
            queryFn: () => getRouteHeaderExistsD365(routeId, dataAreaId),
            enabled: Boolean(routeId) && Boolean(dataAreaId),
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const routeExistsMap = useMemo(() => {
        const map = new Map<string, boolean | null>();
        routeOperationKeys.forEach((key, index) => {
            const q = routeExistsQueries[index];
            map.set(key.rpKey, q?.isFetched ? (q.data === true) : null);
        });
        return map;
    }, [routeOperationKeys, routeExistsQueries]);

    const routeOpPropertyQueries = useQueries({
        queries: routeOperationKeys.map(({ csRouteID }) => ({
            queryKey: ["GetRouteOperationPropertiesCS", csRouteID],
            queryFn: () => getRouteOperationPropertiesCS(csRouteID),
            enabled: csRouteID > 0,
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const routeOpPropertyMap = useMemo(() => {
        const map = new Map<string, IRouteOperationPropertyCS[]>();
        routeOperationKeys.forEach((key, index) => {
            const q = routeOpPropertyQueries[index];
            map.set(key.rpKey, q?.data ?? []);
        });
        return map;
    }, [routeOperationKeys, routeOpPropertyQueries]);

    const routeOpRequirementQueries = useQueries({
        queries: routeOperationKeys.map(({ csRouteID }) => ({
            queryKey: ["GetRouteOperationRequirementsCS", csRouteID],
            queryFn: () => getRouteOperationRequirementsCS(csRouteID),
            enabled: csRouteID > 0,
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const routeOpRequirementMap = useMemo(() => {
        const map = new Map<string, IRouteOperationRequirementCS[]>();
        routeOperationKeys.forEach((key, index) => {
            const q = routeOpRequirementQueries[index];
            map.set(key.rpKey, q?.data ?? []);
        });
        return map;
    }, [routeOperationKeys, routeOpRequirementQueries]);

    const bomLineQueries = useQueries({
        queries: routeOperationKeys.map(({ csRouteID }) => ({
            queryKey: ["GetBomLinesByRouteCS", csRouteID],
            queryFn: () => getBomLinesByRouteCS(csRouteID),
            enabled: csRouteID > 0,
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const bomLineMap = useMemo(() => {
        const map = new Map<string, IBomLineCS[]>();
        routeOperationKeys.forEach((key, index) => {
            const q = bomLineQueries[index];
            map.set(key.rpKey, q?.data ?? []);
        });
        return map;
    }, [routeOperationKeys, bomLineQueries]);

    // ---------- Route header + version comparison queries ----------
    const routeHeaderComparisonQueries = useQueries({
        queries: routeOperationKeys.map(({ csRouteID, routeId, dataAreaId }) => ({
            queryKey: ["GetRouteHeaderComparison", { csRouteID, routeId, dataAreaId }],
            queryFn: () => getRouteHeaderComparison(csRouteID, routeId, dataAreaId),
            enabled: csRouteID > 0 && Boolean(routeId) && Boolean(dataAreaId),
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const routeVersionComparisonQueries = useQueries({
        queries: routeOperationKeys.map(({ rpKey, csRouteID, routeId, dataAreaId }) => {
            const itemNumber = rpKey.split("|")[0];
            const rv = routeVersionMap.get(rpKey);
            return {
                queryKey: ["GetRouteVersionComparison", { csRouteID, routeId, dataAreaId, itemNumber }],
                queryFn: () => getRouteVersionComparison(csRouteID, routeId, dataAreaId, rv?.productionSiteId ?? "", itemNumber),
                enabled: csRouteID > 0 && Boolean(routeId) && Boolean(dataAreaId) && Boolean(itemNumber),
                refetchOnWindowFocus: false,
                retry: false,
            };
        }),
    });

    const routeComparisonMap = useMemo(() => {
        const map = new Map<string, { headerData?: IComparisonInfo[]; versionData?: IComparisonInfo[]; isLoading: boolean }>();
        routeOperationKeys.forEach((key, index) => {
            const hq = routeHeaderComparisonQueries[index];
            const vq = routeVersionComparisonQueries[index];
            map.set(key.rpKey, {
                headerData: hq?.data,
                versionData: vq?.data,
                isLoading: (hq?.isLoading ?? true) || (vq?.isLoading ?? true),
            });
        });
        return map;
    }, [routeOperationKeys, routeHeaderComparisonQueries, routeVersionComparisonQueries]);

    // ---------- BOM-sourced Released Product existence + comparison ----------
    const bomRPExistenceKeys = useMemo(() => {
        const keys: { itemNumber: string; dataAreaId: string }[] = [];
        const seen = new Set<string>();
        for (const bomKey of expandedBomLines) {
            if (seen.has(bomKey)) {
                continue;
            }
            seen.add(bomKey);
            const [itemNumber, dataAreaId] = bomKey.split("|");
            if (itemNumber && dataAreaId) {
                keys.push({ itemNumber, dataAreaId });
            }
        }
        return keys;
    }, [expandedBomLines]);

    const bomRPExistenceQueries = useQueries({
        queries: bomRPExistenceKeys.map(({ itemNumber, dataAreaId }) => ({
            queryKey: ["GetReleasedProductExistsD365", { itemNumber, dataAreaId }],
            queryFn: () => getReleasedProductExistsD365(itemNumber, dataAreaId),
            enabled: Boolean(itemNumber && dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const bomRPExistsMap = useMemo(() => {
        const map = new Map<string, boolean | null>();
        bomRPExistenceKeys.forEach((key, index) => {
            const q = bomRPExistenceQueries[index];
            map.set(`${key.itemNumber}|${key.dataAreaId}`, q?.isFetched ? (q.data === true) : null);
        });
        return map;
    }, [bomRPExistenceKeys, bomRPExistenceQueries]);

    const bomRPComparisonKeys = useMemo(() => {
        const keys: { itemNumber: string; dataAreaId: string }[] = [];
        const seen = new Set<string>();
        for (const bomKey of expandedBomLines) {
            if (seen.has(bomKey)) {
                continue;
            }
            seen.add(bomKey);
            const [itemNumber, dataAreaId] = bomKey.split("|");
            if (!itemNumber || !dataAreaId) {
                continue;
            }
            // Skip when CS has no route version for this item — phantom inline BOM items aren't
            // standalone released products, the comparison resolve will fail.
            if (!routeVersionMap.get(bomKey)) {
                continue;
            }
            // Check if this BOM line is a "make" item (stdSailItem === true)
            let isMakeItem = false;
            for (const bomLines of bomLineMap.values()) {
                const bl = bomLines.find((b) => b.itemNumber === itemNumber && b.dataAreaID === dataAreaId);
                if (bl?.stdSailItem) {
                    isMakeItem = true;
                    break;
                }
            }
            if (isMakeItem) {
                keys.push({ itemNumber, dataAreaId });
            }
        }
        return keys;
    }, [expandedBomLines, bomLineMap, routeVersionMap]);

    const bomRPComparisonQueries = useQueries({
        queries: bomRPComparisonKeys.map(({ itemNumber, dataAreaId }) => ({
            queryKey: ["GetReleasedProductComparison", { itemNumber, dataAreaId }],
            queryFn: () =>
                getReleasedProductComparison({
                    keys: { itemNumber, dataAreaId, itemID: 0 },
                }),
            enabled: Boolean(itemNumber && dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const bomRPComparisonMap = useMemo(
        () => buildComparisonQueryMap(bomRPComparisonKeys, bomRPComparisonQueries, (k) => `${k.itemNumber}|${k.dataAreaId}`),
        [bomRPComparisonKeys, bomRPComparisonQueries],
    );

    // ---------- D365 fallback chain ----------
    // Phantom BOM-sourced RP keys whose CS routeVersion fetch resolved with null.
    // These are candidates for the D365 fallback chain.
    // d365BomLineMapRef.current is read (not listed as dep) to break the cyclic dependency;
    // d365BomLineMapVersion is the trigger that causes recomputation when the ref is updated.
    const d365FallbackRPKeys = useMemo(() => {
        const keys: { itemNumber: string; dataAreaId: string }[] = [];
        const seen = new Set<string>();
        for (const bomKey of expandedBomLines) {
            if (seen.has(bomKey)) {
                continue;
            }
            seen.add(bomKey);
            // Only fall back when CS resolved null (NOT undefined — undefined means still loading)
            if (routeVersionMap.has(bomKey) && routeVersionMap.get(bomKey) === null) {
                const [itemNumber, dataAreaId] = bomKey.split("|");
                let isPhantom = false;
                // Check CS bomLineMap (b.dataAreaID — capital ID)
                for (const bomLines of bomLineMap.values()) {
                    const bl = bomLines.find((b) => b.itemNumber === itemNumber && b.dataAreaID === dataAreaId);
                    if (bl?.stdSailItem) {
                        isPhantom = true;
                        break;
                    }
                }
                // Also check D365 bomLineMap via ref to avoid cyclic dep (b.dataAreaId — lowercase)
                if (!isPhantom) {
                    for (const d365BomLines of d365BomLineMapRef.current.values()) {
                        const d365bl = d365BomLines.find((b) => b.itemNumber === itemNumber && b.dataAreaId === dataAreaId);
                        if (d365bl?.lineType === "Phantom") {
                            isPhantom = true;
                            break;
                        }
                    }
                }
                if (isPhantom && itemNumber && dataAreaId) {
                    keys.push({ itemNumber, dataAreaId });
                }
            }
        }
        return keys;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [expandedBomLines, routeVersionMap, bomLineMap, d365BomLineMapVersion]);

    const d365RouteVersionQueries = useQueries({
        queries: d365FallbackRPKeys.map(({ itemNumber, dataAreaId }) => ({
            queryKey: ["GetRouteVersionByItemD365", { itemNumber, dataAreaId }],
            queryFn: () => getRouteVersionByItemD365(itemNumber, dataAreaId),
            enabled: Boolean(itemNumber && dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const d365RouteVersionMap = useMemo(() => {
        const map = new Map<string, IRouteVersionD365 | null>();
        d365FallbackRPKeys.forEach((key, index) => {
            const q = d365RouteVersionQueries[index];
            if (q?.isFetched) {
                map.set(`${key.itemNumber}|${key.dataAreaId}`, q.data ?? null);
            }
        });
        return map;
    }, [d365FallbackRPKeys, d365RouteVersionQueries]);

    const d365RouteKeys = useMemo(() => {
        const keys: { rpKey: string; routeId: string; dataAreaId: string }[] = [];
        for (const [rpKey, rv] of d365RouteVersionMap) {
            if (rv?.routeId && rv?.dataAreaId) {
                keys.push({ rpKey, routeId: rv.routeId, dataAreaId: rv.dataAreaId });
            }
        }
        return keys;
    }, [d365RouteVersionMap]);

    const d365RouteOperationQueries = useQueries({
        queries: d365RouteKeys.map(({ routeId, dataAreaId }) => ({
            queryKey: ["GetRouteOperationsByRouteD365", { routeId, dataAreaId }],
            queryFn: () => getRouteOperationsByRouteD365(routeId, dataAreaId),
            enabled: Boolean(routeId) && Boolean(dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const d365RouteOperationsMap = useMemo(() => {
        const map = new Map<string, { routeId: string; operations: IRouteOperationD365[]; isLoading: boolean; isError: boolean }>();
        d365RouteKeys.forEach((key, index) => {
            const q = d365RouteOperationQueries[index];
            const raw = q?.data;
            const operations = raw && raw.length > 0
                ? [...raw].sort((a, b) => a.operationNumber - b.operationNumber)
                : EMPTY_OPERATIONS_D365;
            map.set(key.rpKey, {
                routeId: key.routeId,
                operations,
                isLoading: q?.isLoading ?? true,
                isError: q?.isError ?? false,
            });
        });
        return map;
    }, [d365RouteKeys, d365RouteOperationQueries]);

    const d365OpPropertyQueries = useQueries({
        queries: d365RouteKeys.map(({ routeId, dataAreaId }) => ({
            queryKey: ["GetRouteOperationPropertiesByRouteD365", { routeId, dataAreaId }],
            queryFn: () => getRouteOperationPropertiesByRouteD365(routeId, dataAreaId),
            enabled: Boolean(routeId) && Boolean(dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const d365OpPropertyMap = useMemo(() => {
        const map = new Map<string, { data: IRouteOperationPropertyD365[]; isLoading: boolean; isFetched: boolean }>();
        d365RouteKeys.forEach((key, index) => {
            const q = d365OpPropertyQueries[index];
            map.set(key.rpKey, {
                data: q?.data ?? EMPTY_OP_PROPERTIES_D365,
                isLoading: q?.isLoading ?? false,
                isFetched: q?.isFetched ?? false,
            });
        });
        return map;
    }, [d365RouteKeys, d365OpPropertyQueries]);

    const d365OpRequirementQueries = useQueries({
        queries: d365RouteKeys.map(({ routeId, dataAreaId }) => ({
            queryKey: ["GetRouteOperationRequirementsByRouteD365", { routeId, dataAreaId }],
            queryFn: () => getRouteOperationRequirementsByRouteD365(routeId, dataAreaId),
            enabled: Boolean(routeId) && Boolean(dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const d365OpRequirementMap = useMemo(() => {
        const map = new Map<string, IRouteOperationRequirementD365[]>();
        d365RouteKeys.forEach((key, index) => {
            map.set(key.rpKey, d365OpRequirementQueries[index]?.data ?? EMPTY_OP_REQUIREMENTS_D365);
        });
        return map;
    }, [d365RouteKeys, d365OpRequirementQueries]);

    const d365BomVersionQueries = useQueries({
        queries: d365RouteKeys.map(({ rpKey, dataAreaId }) => {
            const itemNumber = rpKey.split("|")[0];
            return {
                queryKey: ["GetBomVersionByItemD365", { itemNumber, dataAreaId }],
                queryFn: () => getBomVersionByItemD365(itemNumber, dataAreaId),
                enabled: Boolean(itemNumber && dataAreaId),
                staleTime: 5 * 60 * 1000,
                refetchOnWindowFocus: false,
                retry: false,
            };
        }),
    });

    const d365BomVersionMap = useMemo(() => {
        const map = new Map<string, IBomVersionD365 | null>();
        d365RouteKeys.forEach((key, index) => {
            const q = d365BomVersionQueries[index];
            if (q?.isFetched) {
                map.set(key.rpKey, q.data ?? null);
            }
        });
        return map;
    }, [d365RouteKeys, d365BomVersionQueries]);

    const d365BomKeys = useMemo(() => {
        const keys: { rpKey: string; bomId: string; dataAreaId: string }[] = [];
        for (const [rpKey, bv] of d365BomVersionMap) {
            if (bv?.bomId && bv?.dataAreaId) {
                keys.push({ rpKey, bomId: bv.bomId, dataAreaId: bv.dataAreaId });
            }
        }
        return keys;
    }, [d365BomVersionMap]);

    const d365BomLineQueries = useQueries({
        queries: d365BomKeys.map(({ bomId, dataAreaId }) => ({
            queryKey: ["GetBomLinesByBomD365", { bomId, dataAreaId }],
            queryFn: () => getBomLinesByBomD365(bomId, dataAreaId),
            enabled: Boolean(bomId) && Boolean(dataAreaId),
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: false,
        })),
    });

    const d365BomLineMap = useMemo(() => {
        const map = new Map<string, IBomLineD365[]>();
        d365BomKeys.forEach((key, index) => {
            map.set(key.rpKey, d365BomLineQueries[index]?.data ?? EMPTY_BOM_LINES_D365);
        });
        return map;
    }, [d365BomKeys, d365BomLineQueries]);

    // Sync ref and bump version counter whenever d365BomLineMap content changes.
    // CONTENT comparison (not reference) is required: the map is recreated every render
    // by upstream useMemo+useQueries chains even when no data actually changed. Bumping
    // version on every reference change would cause an infinite render loop because
    // d365FallbackRPKeys depends on the version, and its output cascades back through the
    // chain to produce a new d365BomLineMap reference.
    useEffect(() => {
        const prev = d365BomLineMapRef.current;
        let changed = d365BomLineMap.size !== prev.size;
        if (!changed) {
            for (const [key, lines] of d365BomLineMap) {
                const prevLines = prev.get(key);
                if (!prevLines || prevLines !== lines) {
                    changed = true;
                    break;
                }
            }
        }
        if (changed) {
            d365BomLineMapRef.current = d365BomLineMap;
            setD365BomLineMapVersion((v) => v + 1);
        }
    }, [d365BomLineMap]);

    // ---------- FM Manufacturing Route ----------
    const mItemID = init?.mItemID ?? 0;
    const fmRouteQuery = useQuery({
        queryKey: ["GetManufacturingRoute", mItemID],
        queryFn: () => getManufacturingRoute(mItemID),
        enabled: mItemID > 0,
        staleTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: false,
    });

    // Build raw graph
    const rawGraph = useMemo(() => {
        if (!init || !init.lineNumber) {
            return { nodes: [] as ExplorerNode[], edges: [] as ExplorerEdge[] };
        }

        const lotKey = init.inventoryLotId?.toLowerCase();
        const existence = lotKey ? existenceMap.get(lotKey) : undefined;

        // Root: manufacturing item line detail node
        const lineNodeId = `SalesOrderLineDetail-${init.lineNumber}`;
        const rootNode: ExplorerNode = {
            id: lineNodeId,
            type: "lineDetailNode",
            position: { x: 0, y: 0 },
            data: {
                entityType: "SalesOrderLineDetail",
                label: `Manufacturing Item ${init.coOrderItemID}`,
                nodeTitle: "Manufacturing Item",
                nodeSubtitle: init.coOrderItemID,
                status: "synced",
                summaryFields: [],
                lineDetail: {
                    lineNumber: init.lineNumber,
                    status: init.salesOrderLineStatus,
                    itemNumber: init.itemNumber,
                    externalItemNumber: init.externalItemNumber,
                    lineDescription: init.lineName || init.itemDescription,
                    quantity: init.orderedSalesQuantity,
                    salesUnitSymbol: init.salesUnitSymbol,
                    salesPrice: init.salesPrice,
                    lineAmount: init.lineAmount,
                    currencyCode: init.currencyCode,
                    inventoryLotId: init.inventoryLotId,
                    dataAreaId: init.dataAreaId,
                    orderID: init.orderID,
                    existsInCS: existence?.existsInCS,
                    existsInD365: existence?.existsInD365,
                },
                isSelected: false,
                onSelect: onNodeSelect,
            },
        };

        const productNodes: ExplorerNode[] = [];
        const productEdges: ExplorerEdge[] = [];
        const releasedProductNodes: ExplorerNode[] = [];
        const releasedProductEdges: ExplorerEdge[] = [];

        if (init.itemNumber) {
            const productNumber = init.itemNumber;
            const isMake = productMakeMap.get(productNumber) ?? false;

            if (isMake) {
                // Make path: Product comparison + Released Products list
                const pQuery = productMap.get(productNumber);
                const pStatus = pQuery
                    ? computeStatus(pQuery.data, pQuery.isLoading, pQuery.isError, pQuery.isFetched)
                    : { status: "na" as const, discrepancyCount: 0, totalFields: 0 };
                const d365Exists = pQuery?.isFetched
                    ? (pQuery.data !== undefined && !pQuery.isError)
                    : null;

                const productNodeId = `Product-${productNumber}`;
                productNodes.push({
                    id: productNodeId,
                    type: "entityNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "Product" as const,
                        label: `Product ${productNumber}`,
                        nodeTitle: "Product",
                        nodeSubtitle: productNumber,
                        status: pStatus.status,
                        summaryFields: [
                            { label: "Product Number", value: productNumber },
                        ],
                        discrepancyCount: pStatus.discrepancyCount,
                        totalFields: pStatus.totalFields,
                        isSelected: false,
                        onSelect: onNodeSelect,
                        d365Exists,
                    },
                } as ExplorerNode);

                productEdges.push({
                    id: `e-prod-${init.lineNumber}`,
                    source: lineNodeId,
                    target: productNodeId,
                });

                // Released Products list node — multi-company resolution from designID
                const rpListNodeId = `ReleasedProducts-${productNumber}`;
                const designID = init.designID ?? 0;
                const designItems = designID > 0 ? itemsByDesignMap.get(designID) : undefined;

                const rpItems = designItems && designItems.length > 0
                    ? designItems.map((item, idx) => {
                        const mapKey = `${item.name}|${item.entity}`;
                        const rpQuery = releasedProductMap.get(mapKey);
                        const comparisonOk = rpQuery?.data !== undefined && !rpQuery?.isError;

                        return {
                            itemNumber: item.name,
                            dataAreaId: item.entity,
                            lineNumber: idx + 1,
                            make: item.make,
                            existsInCS: rpQuery?.isFetched ? comparisonOk : undefined,
                            existsInD365: rpQuery?.isFetched ? comparisonOk : undefined,
                        };
                    })
                    : [(() => {
                        const dataAreaId = init.dataAreaId ?? "";
                        const mapKey = `${productNumber}|${dataAreaId}`;
                        const rpQuery = releasedProductMap.get(mapKey);
                        const comparisonOk = rpQuery?.data !== undefined && !rpQuery?.isError;
                        return {
                            itemNumber: productNumber,
                            dataAreaId,
                            lineNumber: init.lineNumber,
                            existsInCS: rpQuery?.isFetched ? comparisonOk : undefined,
                            existsInD365: rpQuery?.isFetched ? comparisonOk : undefined,
                        };
                    })()];

                const anyLoading = rpItems.some((i) => i.existsInCS === undefined);
                const overallStatus: ExplorerNodeStatus = anyLoading ? "loading" : "synced";

                releasedProductNodes.push({
                    id: rpListNodeId,
                    type: "releasedProductListNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "ReleasedProduct" as const,
                        label: "Released Products",
                        nodeTitle: "Released Products",
                        nodeSubtitle: productNumber,
                        status: overallStatus,
                        summaryFields: [],
                        releasedProducts: rpItems,
                        expandedReleasedProducts,
                        isSelected: false,
                        onSelect: onNodeSelect,
                        onReleasedProductSelect: onReleasedProductSelect,
                    },
                } as ExplorerNode);

                releasedProductEdges.push({
                    id: `e-rp-${productNumber}`,
                    source: productNodeId,
                    target: rpListNodeId,
                });
            } else {
                // Buy/stock path: D365-only Product + single RP entity nodes
                const buyQuery = buyProductMap.get(productNumber);
                const pStatus: { status: ExplorerNodeStatus; discrepancyCount: number; totalFields: number } = buyQuery?.isLoading
                    ? { status: "loading" as const, discrepancyCount: 0, totalFields: 0 }
                    : buyQuery?.isError
                        ? { status: "error" as const, discrepancyCount: 0, totalFields: 0 }
                        : { status: "na" as const, discrepancyCount: 0, totalFields: 0 };
                const productD365Exists = buyQuery?.data ? true : buyQuery?.isError ? false : undefined;

                const productNodeId = `Product-${productNumber}`;
                productNodes.push({
                    id: productNodeId,
                    type: "entityNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "Product" as const,
                        label: `Product ${productNumber}`,
                        nodeTitle: "Product",
                        nodeSubtitle: productNumber,
                        status: pStatus.status,
                        summaryFields: [
                            { label: "Product Number", value: productNumber },
                        ],
                        discrepancyCount: pStatus.discrepancyCount,
                        totalFields: pStatus.totalFields,
                        isSelected: false,
                        onSelect: onNodeSelect,
                        d365Exists: productD365Exists,
                    },
                } as ExplorerNode);

                productEdges.push({
                    id: `e-prod-${init.lineNumber}`,
                    source: lineNodeId,
                    target: productNodeId,
                });

                // Single Released Product entity node from the line's company
                const dataAreaId = init.dataAreaId ?? "";
                const rpNodeId = `ReleasedProduct-${productNumber}|${dataAreaId}`;
                const buyRPQuery = buyRPMap.get(`${productNumber}|${dataAreaId}`);
                const buyRPStatus: ExplorerNodeStatus = buyRPQuery?.isLoading
                    ? "loading"
                    : buyRPQuery?.isError
                        ? "error"
                        : "na";

                releasedProductNodes.push({
                    id: rpNodeId,
                    type: "entityNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "ReleasedProduct" as const,
                        label: "Released Product",
                        nodeTitle: "Released Product",
                        nodeSubtitle: productNumber,
                        status: buyRPStatus,
                        dataAreaId,
                        summaryFields: [
                            { label: "Product Number", value: productNumber },
                            { label: "Company", value: dataAreaId },
                        ],
                        discrepancyCount: 0,
                        totalFields: 0,
                        isSelected: false,
                        onSelect: onNodeSelect,
                        d365Exists: buyRPQuery?.data ? true : buyRPQuery?.isError ? false : undefined,
                    },
                } as ExplorerNode);

                releasedProductEdges.push({
                    id: `e-rp-${productNumber}`,
                    source: productNodeId,
                    target: rpNodeId,
                });
            }
        }

        // Expanded Released Product entity nodes — individual RP nodes that fan out from the list
        const expandedRPNodes: ExplorerNode[] = [];
        const expandedRPEdges: ExplorerEdge[] = [];
        const routeNodes: ExplorerNode[] = [];
        const routeEdges: ExplorerEdge[] = [];
        const visitedRPKeys = new Set<string>();

        /** Create a Route → OpDetail → BomLines chain for a given RP key. */
        function buildRouteChain(rpKey: string, sourceNodeId: string, dataAreaId: string, edgePrefix: string) {
            const resolvedRoute = routeVersionMap.get(rpKey);
            if (!resolvedRoute) {
                return;
            }

            const routeDataAreaId = resolvedRoute.dataAreaID || dataAreaId;
            const routeNodeId = `Routes-${rpKey}`;
            const routeInfo = routeDataMap.get(rpKey);
            const opsLoading = routeInfo?.isLoading ?? true;
            const opsError = routeInfo?.isError ?? false;
            const routeStatus: ExplorerNodeStatus = opsLoading ? "loading" : opsError ? "error" : "synced";

            routeNodes.push({
                id: routeNodeId,
                type: "routeOperationsListNode",
                position: { x: 0, y: 0 },
                data: {
                    entityType: "Route" as const,
                    label: "Route",
                    nodeTitle: "Route",
                    nodeSubtitle: resolvedRoute.routeId,
                    status: routeStatus,
                    dataAreaId: routeDataAreaId,
                    summaryFields: [],
                    routeId: resolvedRoute.routeId,
                    routeVersionSiteId: resolvedRoute.productionSiteId || undefined,
                    routeOperations: (routeInfo?.operations ?? [])
                        .map((op) => ({
                            operationNumber: op.operationNumber,
                            operationId: op.operationId,
                            operationPriority: op.operationPriority,
                            nextOperationNumber: op.nextRouteOperationNumber,
                            isExpanded: expandedOperations.includes(`${rpKey}|${op.operationNumber}`),
                        })),
                    isSelected: false,
                    onSelect: onNodeSelect,
                    onOperationSelect: (operationNumber: number) => onOperationSelect(rpKey, operationNumber),
                    d365Exists: routeExistsMap.get(rpKey) ?? null,
                },
            } as ExplorerNode);

            routeEdges.push({
                id: `e-${edgePrefix}route-${rpKey}`,
                source: sourceNodeId,
                target: routeNodeId,
            });

            const opProperties = routeOpPropertyMap.get(rpKey) ?? [];
            const opRequirements = routeOpRequirementMap.get(rpKey) ?? [];
            const allBomLines = bomLineMap.get(rpKey) ?? [];

            for (const opKey of expandedOperations) {
                if (!opKey.startsWith(rpKey + "|")) {
                    continue;
                }
                const operationNumber = parseInt(opKey.split("|")[2], 10);
                if (isNaN(operationNumber)) {
                    continue;
                }

                const op = (routeInfo?.operations ?? []).find((o) => o.operationNumber === operationNumber);
                if (!op) {
                    continue;
                }

                const prop = opProperties.find(
                    (p) => p.operationNumber === operationNumber && p.operationId === op.operationId,
                );
                const reqs = opRequirements.filter(
                    (r) => r.operationNumber === operationNumber && r.routeOperationId === op.operationId,
                );

                const opDetailNodeId = `OpDetail-${opKey}`;

                routeNodes.push({
                    id: opDetailNodeId,
                    type: "operationDetailNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "RouteOperationDetail" as const,
                        label: `Operation ${op.operationId}`,
                        nodeTitle: "Operation",
                        nodeSubtitle: splitPascalCase(op.operationId),
                        status: prop ? "synced" : "loading",
                        dataAreaId: routeDataAreaId,
                        summaryFields: [],
                        isSelected: false,
                        onSelect: onNodeSelect,
                        operationProperty: prop
                            ? {
                                operationId: prop.operationId,
                                operationNumber: prop.operationNumber,
                                operationPriority: prop.operationPriority,
                                routeGroupId: prop.routeGroupId,
                                routeType: prop.routeType,
                                processTime: prop.processTime,
                                setupTime: prop.setupTime,
                                queueTimeBefore: prop.queueTimeBefore,
                                queueTimeAfter: prop.queueTimeAfter,
                                transitTime: prop.transitTime,
                                loadPercentage: prop.loadPercentage,
                                resourceQuantity: prop.resourceQuantity,
                                processQuantity: prop.processQuantity,
                                costingOperationResourceId: prop.costingOperationResourceId,
                                processCostCategoryId: prop.processCostCategoryId,
                                setupCostCategoryId: prop.setupCostCategoryId,
                                quantityCostCategoryId: prop.quantityCostCategoryId,
                                productionSiteId: prop.productionSiteId,
                                itemNumber: prop.itemNumber,
                            }
                            : undefined,
                        operationRequirements: reqs.map((r) => ({
                            operationNumber: r.operationNumber,
                            routeOperationId: r.routeOperationId,
                            requiredOperationsResourceGroupId: r.requiredOperationsResourceGroupId,
                            requiredOperationsResourceId: r.requiredOperationsResourceId,
                            requiredCapabilityName: r.requiredCapabilityName,
                            willJobSchedulingUseRequirement: r.willJobSchedulingUseRequirement,
                            willOperationSchedulingUseRequirement: r.willOperationSchedulingUseRequirement,
                        })),
                    },
                } as ExplorerNode);

                routeEdges.push({
                    id: `e-${edgePrefix}opdetail-${opKey}`,
                    source: routeNodeId,
                    target: opDetailNodeId,
                });

                // BOM lines for this operation
                const opBomLines = allBomLines.filter(
                    (bl) => bl.routeOperationNumber === operationNumber,
                );

                if (opBomLines.length > 0) {
                    const bomNodeId = `BomLines-${opKey}`;

                    routeNodes.push({
                        id: bomNodeId,
                        type: "bomLinesListNode",
                        position: { x: 0, y: 0 },
                        data: {
                            entityType: "BomLines" as const,
                            label: "BOM Lines",
                            nodeTitle: "BOM",
                            nodeSubtitle: splitPascalCase(op.operationId),
                            status: "synced" as const,
                            dataAreaId: routeDataAreaId,
                            summaryFields: [],
                            isSelected: false,
                            onSelect: onNodeSelect,
                            onBomLineSelect,
                            bomLines: opBomLines.map((bl) => ({
                                itemNumber: bl.itemNumber,
                                dataAreaId: bl.dataAreaID,
                                quantity: bl.quantity,
                                productUnitSymbol: bl.productUnitSymbol,
                                lineType: bl.lineType,
                                flushingPrinciple: bl.flushingPrinciple,
                                consumptionWarehouseId: bl.consumptionWarehouseId,
                                vendorAccountNumber: bl.vendorAccountNumber,
                                routeOperationNumber: bl.routeOperationNumber,
                                stdSailItem: bl.stdSailItem,
                                isExpanded: expandedBomLines.includes(`${bl.itemNumber}|${bl.dataAreaID}`),
                            })),
                        },
                    } as ExplorerNode);

                    routeEdges.push({
                        id: `e-${edgePrefix}bom-${opKey}`,
                        source: opDetailNodeId,
                        target: bomNodeId,
                    });

                    expandBomLineItems(opBomLines, bomNodeId, opKey);
                }
            }
        }

        /** Create a D365-sourced Route → OpDetail → BomLines chain for a given RP key. */
        function buildD365RouteChain(rpKey: string, sourceNodeId: string, dataAreaId: string) {
            const rv = d365RouteVersionMap.get(rpKey);
            if (!rv) {
                return;
            }

            const routeDataAreaId = rv.dataAreaId || dataAreaId;
            const routeNodeId = `Routes-${rpKey}`;
            const routeInfo = d365RouteOperationsMap.get(rpKey);
            const opsLoading = routeInfo?.isLoading ?? true;
            const opsError = routeInfo?.isError ?? false;
            const routeStatus: ExplorerNodeStatus = opsLoading ? "loading" : opsError ? "error" : "synced";

            routeNodes.push({
                id: routeNodeId,
                type: "routeOperationsListNode",
                position: { x: 0, y: 0 },
                data: {
                    entityType: "Route" as const,
                    label: "Route",
                    nodeTitle: "Route",
                    nodeSubtitle: rv.routeId,
                    status: routeStatus,
                    dataAreaId: routeDataAreaId,
                    source: "d365" as const,
                    summaryFields: [],
                    routeId: rv.routeId,
                    routeVersionSiteId: rv.productionSiteId || undefined,
                    routeOperations: (routeInfo?.operations ?? []).map((op) => ({
                        operationNumber: op.operationNumber,
                        operationId: op.operationId,
                        operationPriority: op.operationPriority,
                        nextOperationNumber: op.nextRouteOperationNumber,
                        isExpanded: expandedOperations.includes(`${rpKey}|${op.operationNumber}`),
                    })),
                    isSelected: false,
                    onSelect: onNodeSelect,
                    onOperationSelect: (operationNumber: number) => onOperationSelect(rpKey, operationNumber),
                    d365Exists: true,
                },
            } as ExplorerNode);

            routeEdges.push({
                id: `e-d365route-${rpKey}`,
                source: sourceNodeId,
                target: routeNodeId,
            });

            const opPropertiesInfo = d365OpPropertyMap.get(rpKey);
            const opProperties = opPropertiesInfo?.data ?? EMPTY_OP_PROPERTIES_D365;
            const opPropertiesLoading = opPropertiesInfo?.isLoading ?? false;
            const opRequirements = d365OpRequirementMap.get(rpKey) ?? EMPTY_OP_REQUIREMENTS_D365;
            const allBomLines = d365BomLineMap.get(rpKey) ?? EMPTY_BOM_LINES_D365;

            // D365 BOM lines may be assigned to a specific operation (RouteOperationNumber > 0)
            // or unassigned (RouteOperationNumber === 0, the common case for phantom BOMs).
            // Unassigned lines apply to the route as a whole and hang off the Route node;
            // assigned lines hang off their specific operation node.
            const unassignedBomLines = allBomLines.filter((bl) => !bl.routeOperationNumber);
            if (unassignedBomLines.length > 0) {
                const routeBomNodeId = `BomLines-d365-route-${rpKey}`;
                const hasAssignedLines = allBomLines.some((bl) => bl.routeOperationNumber > 0);
                const noteText = hasAssignedLines
                    ? "These BOM lines have no operation assignment in D365 — assigned lines appear under their operation."
                    : "These BOM lines have no operation assignment in D365.";
                routeNodes.push({
                    id: routeBomNodeId,
                    type: "bomLinesListNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "BomLines" as const,
                        label: "BOM Lines",
                        nodeTitle: "BOM",
                        nodeSubtitle: rv.routeId,
                        status: "synced" as const,
                        dataAreaId: routeDataAreaId,
                        source: "d365" as const,
                        summaryFields: [],
                        bomNote: noteText,
                        isSelected: false,
                        onSelect: onNodeSelect,
                        onBomLineSelect,
                        bomLines: unassignedBomLines.map((bl) => ({
                            itemNumber: bl.itemNumber,
                            dataAreaId: bl.dataAreaId,
                            quantity: bl.quantity,
                            productUnitSymbol: bl.productUnitSymbol,
                            lineType: bl.lineType,
                            flushingPrinciple: bl.flushingPrinciple,
                            consumptionWarehouseId: bl.consumptionWarehouseId,
                            vendorAccountNumber: bl.vendorAccountNumber,
                            routeOperationNumber: bl.routeOperationNumber,
                            stdSailItem: bl.lineType === "Phantom",
                            isExpanded: expandedBomLines.includes(`${bl.itemNumber}|${bl.dataAreaId}`),
                        })),
                    },
                } as ExplorerNode);
                routeEdges.push({
                    id: `e-d365bom-route-${rpKey}`,
                    source: routeNodeId,
                    target: routeBomNodeId,
                });
                expandD365BomLineItems(unassignedBomLines, routeBomNodeId, rpKey, dataAreaId);
            }

            for (const opKey of expandedOperations) {
                if (!opKey.startsWith(rpKey + "|")) {
                    continue;
                }
                const operationNumber = parseInt(opKey.split("|")[2], 10);
                if (isNaN(operationNumber)) {
                    continue;
                }

                const op = (routeInfo?.operations ?? []).find((o) => o.operationNumber === operationNumber);
                if (!op) {
                    continue;
                }

                // Match by operationId only — D365's Shea_RouteOperationProperties doesn't
                // populate OperationNumber on every row.
                const prop = opProperties.find((p) => p.operationId === op.operationId);
                const reqs = opRequirements.filter((r) => r.routeOperationId === op.operationId);

                const opDetailNodeId = `OpDetail-${opKey}`;

                const opDetailStatus: ExplorerNodeStatus = opPropertiesLoading
                    ? "loading"
                    : "synced";

                routeNodes.push({
                    id: opDetailNodeId,
                    type: "operationDetailNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "RouteOperationDetail" as const,
                        label: `Operation ${op.operationId}`,
                        nodeTitle: "Operation",
                        nodeSubtitle: splitPascalCase(op.operationId),
                        status: opDetailStatus,
                        dataAreaId: routeDataAreaId,
                        source: "d365" as const,
                        summaryFields: [],
                        isSelected: false,
                        onSelect: onNodeSelect,
                        operationProperty: prop
                            ? {
                                operationId: prop.operationId,
                                operationNumber: prop.operationNumber,
                                operationPriority: prop.operationPriority,
                                routeGroupId: prop.routeGroupId,
                                routeType: prop.routeType,
                                processTime: prop.processTime,
                                setupTime: prop.setupTime,
                                queueTimeBefore: prop.queueTimeBefore,
                                queueTimeAfter: prop.queueTimeAfter,
                                transitTime: prop.transitTime,
                                loadPercentage: prop.loadPercentage,
                                resourceQuantity: prop.resourceQuantity,
                                processQuantity: prop.processQuantity,
                                costingOperationResourceId: prop.costingOperationResourceId,
                                processCostCategoryId: prop.processCostCategoryId,
                                setupCostCategoryId: prop.setupCostCategoryId,
                                quantityCostCategoryId: prop.quantityCostCategoryId,
                                productionSiteId: prop.productionSiteId,
                                itemNumber: prop.itemNumber,
                            }
                            : undefined,
                        operationRequirements: reqs.map((r) => ({
                            operationNumber: r.operationNumber,
                            routeOperationId: r.routeOperationId,
                            requiredOperationsResourceGroupId: r.requiredOperationsResourceGroupId,
                            requiredOperationsResourceId: r.requiredOperationsResourceId,
                            requiredCapabilityName: r.requiredCapabilityName,
                            willJobSchedulingUseRequirement: r.willJobSchedulingUseRequirement,
                            willOperationSchedulingUseRequirement: r.willOperationSchedulingUseRequirement,
                        })),
                    },
                } as ExplorerNode);

                routeEdges.push({
                    id: `e-d365opdetail-${opKey}`,
                    source: routeNodeId,
                    target: opDetailNodeId,
                });

                const opBomLines = allBomLines.filter(
                    (bl) => bl.routeOperationNumber > 0 && bl.routeOperationNumber === operationNumber,
                );

                if (opBomLines.length > 0) {
                    const bomNodeId = `BomLines-d365-${opKey}`;

                    routeNodes.push({
                        id: bomNodeId,
                        type: "bomLinesListNode",
                        position: { x: 0, y: 0 },
                        data: {
                            entityType: "BomLines" as const,
                            label: "BOM Lines",
                            nodeTitle: "BOM",
                            nodeSubtitle: splitPascalCase(op.operationId),
                            status: "synced" as const,
                            dataAreaId: routeDataAreaId,
                            source: "d365" as const,
                            summaryFields: [],
                            isSelected: false,
                            onSelect: onNodeSelect,
                            onBomLineSelect,
                            bomLines: opBomLines.map((bl) => ({
                                itemNumber: bl.itemNumber,
                                dataAreaId: bl.dataAreaId,
                                quantity: bl.quantity,
                                productUnitSymbol: bl.productUnitSymbol,
                                lineType: bl.lineType,
                                flushingPrinciple: bl.flushingPrinciple,
                                consumptionWarehouseId: bl.consumptionWarehouseId,
                                vendorAccountNumber: bl.vendorAccountNumber,
                                routeOperationNumber: bl.routeOperationNumber,
                                stdSailItem: bl.lineType === "Phantom",
                                isExpanded: expandedBomLines.includes(`${bl.itemNumber}|${bl.dataAreaId}`),
                            })),
                        },
                    } as ExplorerNode);

                    routeEdges.push({
                        id: `e-d365bom-${opKey}`,
                        source: opDetailNodeId,
                        target: bomNodeId,
                    });

                    expandD365BomLineItems(opBomLines, bomNodeId, opKey, dataAreaId);
                }
            }
        }

        /** Recursively expand D365 BOM line items into RP → Route → Op → BOM chains */
        function expandD365BomLineItems(
            bomLines: IBomLineD365[],
            parentBomNodeId: string,
            parentOpKey: string,
            _parentDataAreaId: string,
        ) {
            for (const bl of bomLines) {
                const childRpKey = `${bl.itemNumber}|${bl.dataAreaId}`;
                if (!expandedBomLines.includes(childRpKey)) {
                    continue;
                }

                const rpNodeId = `ReleasedProduct-${childRpKey}`;

                routeEdges.push({
                    id: `e-d365bomrp-${parentOpKey}-${bl.itemNumber}`,
                    source: parentBomNodeId,
                    target: rpNodeId,
                });

                if (visitedRPKeys.has(childRpKey) || expandedRPNodes.some((n) => n.id === rpNodeId)) {
                    continue;
                }
                visitedRPKeys.add(childRpKey);

                routeNodes.push({
                    id: rpNodeId,
                    type: "entityNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "ReleasedProduct" as const,
                        label: "Released Product",
                        nodeTitle: "Released Product",
                        nodeSubtitle: bl.itemNumber,
                        status: "synced",
                        dataAreaId: bl.dataAreaId,
                        source: "d365" as const,
                        summaryFields: [
                            { label: "Product Number", value: bl.itemNumber },
                        ],
                        discrepancyCount: 0,
                        totalFields: 0,
                        isSelected: false,
                        onSelect: onNodeSelect,
                        d365Exists: true,
                    },
                } as ExplorerNode);

                // Recurse — only if this child BOM line is a phantom in D365
                if (bl.lineType === "Phantom") {
                    buildD365RouteChain(childRpKey, rpNodeId, bl.dataAreaId);
                }
            }
        }

        /** Recursively expand BOM line items into RP → Route → Op → BOM chains (CS-sourced) */
        function expandBomLineItems(
            bomLines: IBomLineCS[],
            parentBomNodeId: string,
            parentOpKey: string,
        ) {
            for (const bl of bomLines) {
                const rpKey = `${bl.itemNumber}|${bl.dataAreaID}`;
                if (!expandedBomLines.includes(rpKey)) {
                    continue;
                }

                const rpNodeId = `ReleasedProduct-${rpKey}`;

                routeEdges.push({
                    id: `e-bomrp-${parentOpKey}-${bl.itemNumber}`,
                    source: parentBomNodeId,
                    target: rpNodeId,
                });

                if (visitedRPKeys.has(rpKey) || expandedRPNodes.some((n) => n.id === rpNodeId)) {
                    continue;
                }
                visitedRPKeys.add(rpKey);

                const d365Exists = bomRPExistsMap.get(rpKey) ?? null;
                const bomRPStatus: ExplorerNodeStatus = d365Exists === null
                    ? "loading"
                    : d365Exists ? "synced" : "missing";

                routeNodes.push({
                    id: rpNodeId,
                    type: "entityNode",
                    position: { x: 0, y: 0 },
                    data: {
                        entityType: "ReleasedProduct" as const,
                        label: "Released Product",
                        nodeTitle: "Released Product",
                        nodeSubtitle: bl.itemNumber,
                        status: bomRPStatus,
                        dataAreaId: bl.dataAreaID,
                        summaryFields: [
                            { label: "Product Number", value: bl.itemNumber },
                        ],
                        discrepancyCount: 0,
                        totalFields: 0,
                        isSelected: false,
                        onSelect: onNodeSelect,
                        d365Exists,
                    },
                } as ExplorerNode);

                // Decide which chain to build for this BOM-sourced RP
                const csRouteVersion = routeVersionMap.get(rpKey);
                if (csRouteVersion) {
                    buildRouteChain(rpKey, rpNodeId, bl.dataAreaID, "bom");
                } else if (csRouteVersion === null && bl.stdSailItem) {
                    // CS resolved null AND BOM line is phantom — try D365 fallback
                    buildD365RouteChain(rpKey, rpNodeId, bl.dataAreaID);
                }
                // Else: still loading (csRouteVersion === undefined), or non-phantom non-CS-routed item.
            }
        }

        for (const rpKey of expandedReleasedProducts) {
            const [itemNumber, dataAreaId] = rpKey.split("|");
            if (!itemNumber || !dataAreaId) {
                continue;
            }
            const parentListNode = releasedProductNodes.find((n) => {
                const items = n.data.releasedProducts ?? [];
                return items.some((i) => i.itemNumber === itemNumber && i.dataAreaId === dataAreaId);
            });
            if (!parentListNode) {
                continue;
            }
            const rpItem = (parentListNode.data.releasedProducts ?? [])
                .find((i) => i.itemNumber === itemNumber && i.dataAreaId === dataAreaId);

            const mapKey = `${itemNumber}|${dataAreaId}`;
            const rpQuery = releasedProductMap.get(mapKey);
            const rpStatus = rpQuery
                ? computeStatus(rpQuery.data, rpQuery.isLoading, rpQuery.isError, rpQuery.isFetched)
                : { status: "na" as const, discrepancyCount: 0, totalFields: 0 };

            const nodeId = `ReleasedProduct-${rpKey}`;
            const rpCompData = rpQuery?.data;
            const rpProductionType = rpCompData?.find((f) => f.sourcePropertyName === "ProductionType")?.sourceValue ?? null;

            expandedRPNodes.push({
                id: nodeId,
                type: "entityNode",
                position: { x: 0, y: 0 },
                data: {
                    entityType: "ReleasedProduct" as const,
                    label: `Released Product`,
                    nodeTitle: "Released Product",
                    nodeSubtitle: itemNumber,
                    status: rpStatus.status,
                    dataAreaId,
                    summaryFields: [
                        { label: "Production Type", value: rpProductionType as string | null },
                    ],
                    discrepancyCount: rpStatus.discrepancyCount,
                    totalFields: rpStatus.totalFields,
                    isSelected: false,
                    onSelect: onNodeSelect,
                    d365Exists: rpQuery?.isFetched
                        ? (rpQuery.data !== undefined && !rpQuery.isError)
                        : null,
                    make: rpItem?.make,
                },
            } as ExplorerNode);

            expandedRPEdges.push({
                id: `e-rpexp-${rpKey}`,
                source: parentListNode.id,
                target: nodeId,
            });

            buildRouteChain(rpKey, nodeId, dataAreaId, "");
        }

        // FM Manufacturing Route — single node tied to the line root, only when mItemID > 0
        const fmRouteNodes: ExplorerNode[] = [];
        const fmRouteEdges: ExplorerEdge[] = [];
        const isMakeRoot = init.itemNumber ? (productMakeMap.get(init.itemNumber) ?? false) : false;

        if (mItemID > 0 && isMakeRoot) {
            const fmNodeId = `FMRoute-${init.lineNumber}`;
            const fmStatus: ExplorerNodeStatus = fmRouteQuery.isLoading
                ? "loading"
                : fmRouteQuery.isError
                    ? "error"
                    : "na";

            fmRouteNodes.push({
                id: fmNodeId,
                type: "fmRouteNode",
                zIndex: -1,
                position: { x: 0, y: 0 },
                data: {
                    entityType: "FMRoute" as const,
                    label: "FM Manufacturing Route",
                    nodeTitle: "FM Route",
                    nodeSubtitle: init.externalItemNumber || init.itemNumber || `Line ${init.lineNumber}`,
                    status: fmStatus,
                    summaryFields: [],
                    isSelected: false,
                    onSelect: onNodeSelect,
                    fmCurrentStagePercentage: fmRouteQuery.data?.currentStagePercentage,
                    fmStatusForOutput: fmRouteQuery.data?.statusForOutput,
                    fmSiteSteps: fmRouteQuery.data?.siteSteps?.map((step) => ({
                        siteTypeId: step.siteTypeId,
                        siteTypeName: step.siteTypeName,
                        siteName: step.siteName,
                        divisionName: step.divisionName,
                        siteId: step.siteId,
                        divisionId: step.divisionId,
                        countryCode: step.countryCode,
                        startDate: step.startDate,
                        shipDate: step.shipDate,
                    })) ?? [],
                },
            } as ExplorerNode);

            fmRouteEdges.push({
                id: `e-fmroute-${init.lineNumber}`,
                source: lineNodeId,
                sourceHandle: "bottom",
                target: fmNodeId,
                targetHandle: "top",
            });
        }

        const fullIdPrefixes = ["SalesOrderLineDetail-", "ReleasedProduct-", "Product-", "Routes-", "OpDetail-", "BomLines-", "FMRoute-"];
        const allNodes: ExplorerNode[] = [
            rootNode,
            ...productNodes,
            ...releasedProductNodes,
            ...expandedRPNodes,
            ...routeNodes,
            ...fmRouteNodes,
        ].map((n) => ({
            ...n,
            dragHandle: ".node-drag-handle",
            data: {
                ...n.data,
                selectId: fullIdPrefixes.some((p) => n.id.startsWith(p)) ? n.id : n.data.entityType,
            },
        }));

        const allEdges: ExplorerEdge[] = [
            ...productEdges,
            ...releasedProductEdges,
            ...expandedRPEdges,
            ...routeEdges,
            ...fmRouteEdges,
        ];

        return { nodes: allNodes, edges: allEdges };
    }, [
        init,
        existenceMap,
        productMakeMap,
        productMap,
        releasedProductMap,
        buyProductMap,
        buyRPMap,
        itemsByDesignMap,
        expandedReleasedProducts,
        expandedOperations,
        expandedBomLines,
        routeVersionMap,
        routeDataMap,
        routeExistsMap,
        routeOpPropertyMap,
        routeOpRequirementMap,
        bomLineMap,
        bomRPExistsMap,
        d365RouteVersionMap,
        d365RouteOperationsMap,
        d365OpPropertyMap,
        d365OpRequirementMap,
        d365BomLineMap,
        mItemID,
        fmRouteQuery.data,
        fmRouteQuery.isLoading,
        fmRouteQuery.isError,
        onNodeSelect,
        onReleasedProductSelect,
        onOperationSelect,
        onBomLineSelect,
    ]);

    // Layout + node state management
    const [nodesState, setNodesState] = useState<ExplorerNode[]>([]);
    const prevStructureKeyRef = useRef<string>("");

    // Compute structure fingerprint from node/edge IDs only — guards the layout effect
    // from firing every render when rawGraph.nodes is a new reference but the structure
    // (the set of node/edge IDs) hasn't changed. Without this guard the effect creates
    // new node objects every render and re-triggers itself, causing an infinite loop.
    const structureKey = useMemo(() => {
        const nk = rawGraph.nodes.map((n) => n.id).sort().join(",");
        const ek = rawGraph.edges.map((e) => e.id).sort().join(",");
        return `${nk}|${ek}`;
    }, [rawGraph.nodes, rawGraph.edges]);

    useEffect(() => {
        if (structureKey === prevStructureKeyRef.current) {
            return;
        }
        prevStructureKeyRef.current = structureKey;
        const { nodes: layouted } = applyDagreLayout(rawGraph.nodes, rawGraph.edges);
        setNodesState((prev) => {
            const prevMap = new Map(prev.map((n) => [n.id, n]));
            return layouted.map((n) => {
                const existing = prevMap.get(n.id);
                if (existing) {
                    return { ...n, position: existing.position, measured: existing.measured };
                }
                return n;
            });
        });
    }, [structureKey, rawGraph.nodes, rawGraph.edges]);

    // Merge latest data from rawGraph onto nodesState (preserves positions, dimensions, etc.)
    // Also apply isSelected here instead of inside rawGraph to avoid recomputing the entire
    // graph on every selection change.
    const nodes = useMemo(() => {
        const applySelection = (n: ExplorerNode): ExplorerNode => ({
            ...n,
            data: { ...n.data, isSelected: selectedNodeId === n.id },
        });
        if (nodesState.length === 0) {
            // First render before effect fires — compute layout inline
            const result = applyDagreLayout(rawGraph.nodes, rawGraph.edges);
            return result.nodes.map(applySelection);
        }
        const stateMap = new Map(nodesState.map((n) => [n.id, n]));
        return rawGraph.nodes.map((n) => {
            const merged = stateMap.has(n.id)
                ? { ...stateMap.get(n.id)!, data: n.data, dragHandle: n.dragHandle }
                : n;
            return applySelection(merged);
        });
    }, [rawGraph.nodes, rawGraph.edges, nodesState, selectedNodeId]);

    const edges = rawGraph.edges;

    const onNodesChange = useCallback(
        (changes: NodeChange<ExplorerNode>[]) => {
            setNodesState((prev) => applyNodeChanges(changes, prev));
        },
        [],
    );

    const comparisonDataMap = useMemo(() => {
        const map: Record<string, IComparisonInfo[] | undefined> = {};
        for (const [rpKey, rpQuery] of releasedProductMap) {
            map[`ReleasedProduct-${rpKey}`] = rpQuery.data;
        }
        for (const [productNumber, pQuery] of productMap) {
            map[`Product-${productNumber}`] = pQuery.data;
        }
        return map;
    }, [releasedProductMap, productMap]);

    return {
        nodes,
        edges,
        onNodesChange,
        isInitLoading: initLoading,
        comparisonDataMap,
        existenceData,
        routeComparisonMap,
        bomRPComparisonMap,
        bomLineMap,
        buyProductMap,
        buyRPMap,
        init,
    };
}
